import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { api } from 'app/api/client';
import { ToastNotifications } from 'app/services/inAppToastNotifications';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { IPathwayDocument } from '../types/Documents';

export const useDocumentDelete = (pathwayId: string) => {
  const queryClient = useQueryClient();
  const { t } = dynamicTranslations();

  const [documentToDelete, setDocumentToDelete] = useState<IPathwayDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteConfirm = useCallback(async () => {
    if (!documentToDelete) return;
    try {
      setIsDeleting(true);
      await api.del(`rpm/api/v1/pathways/${pathwayId}/documents/${documentToDelete.id}`);
      queryClient.invalidateQueries({ queryKey: [['pathway-documents', pathwayId]] });
      ToastNotifications.displaySuccessNotification(
        t('history.documents.patientSection.toast.deleteSuccess'),
      );
    } catch (error) {
      const status = (error as AxiosError).response?.status;
      const key = status === 409
        ? 'history.documents.patientSection.toast.deleteConflict'
        : 'history.documents.patientSection.toast.deleteError';
      ToastNotifications.displayErrorNotification(t(key));
    } finally {
      setIsDeleting(false);
      setDocumentToDelete(null);
    }
  }, [documentToDelete, pathwayId, queryClient, t]);

  return {
    documentToDelete,
    setDocumentToDelete,
    isDeleting,
    handleDeleteConfirm,
  };
};
